/**
 * marketSocket.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Singleton Socket.IO client for live market data. Used by all client modules
 * that need realtime ticks: Market, WatchList, Orders (open/limit), etc.
 *
 * Two things make this fast:
 *
 *   1. ONE connection across the whole app. Modules subscribe to tokens
 *      through a refcount, so duplicate subscriptions don't hit the server
 *      twice and no token gets dropped while another module still cares
 *      about it.
 *
 *   2. requestAnimationFrame BATCHING. When ticks arrive from Angel One
 *      via Node, they can flood in at 30–60 per second per token. If we
 *      pushed every tick straight into Redux we'd cause 30–60 store updates
 *      per second per token — janky on mobile. Instead, every tick goes
 *      into a buffer (a Map keyed by symbolToken so newer ticks overwrite
 *      older ticks of the same symbol). Once per animation frame the
 *      buffer is flushed to subscribers as a SINGLE Map. The UI then
 *      re-renders at most ~60Hz no matter how fast the firehose is.
 *
 *      This is the same trick most trading terminals use for live ticker
 *      tables, and it keeps the UI butter-smooth even with 100+ scripts.
 *
 *   The fallback behavior (REST polling when socket is down) is handled
 *   inside each saga — see marketSocketSaga.js + the existing market/
 *   watchlist sagas.
 */

import { io } from 'socket.io-client';

// ─── Configuration ───────────────────────────────────────────────────────────

const SOCKET_URL =
    process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';

// Admin-side exchange names → Angel exchangeType integers used by Node.
const EXCHANGE_TO_TYPE = {
    NSE: 1,
    NFO: 2,
    EQUITY: 2,
    'EQUITY(INDEX)': 2,
    BSE: 3,
    BFO: 4,
    MCX: 5,
    NCDEX: 7,
    CDS: 13,
};

// ─── Internal state ──────────────────────────────────────────────────────────

let socket = null;
let connected = false;

const tokenRefs = new Map();             // token -> refcount
const tokenExchangeType = new Map();     // token -> exchangeType (for re-subscribe on reconnect)

const tickBatchListeners = new Set();    // fn(Map<token, LiveTick>)
const statusListeners = new Set();       // fn(boolean)

// rAF batching state
let tickBuffer = new Map();              // token -> latest tick this frame
let rafScheduled = false;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeTick(raw) {
    if (!raw || (!raw.token && !raw.symbolToken)) return null;
    const symbolToken = String(raw.symbolToken ?? raw.token);

    // Convert Node's { bids, asks } (with `qty`) to API-style { buy, sell }
    // (with `quantity`) so consumers can read the tick exactly like the
    // existing REST response.
    let depth;
    if (raw.depth) {
        if (Array.isArray(raw.depth.bids) || Array.isArray(raw.depth.asks)) {
            depth = {
                buy: (raw.depth.bids || []).map((e) => ({
                    price: Number(e.price),
                    quantity: Number(e.qty ?? e.quantity ?? 0),
                    orders: Number(e.orders ?? 0),
                })),
                sell: (raw.depth.asks || []).map((e) => ({
                    price: Number(e.price),
                    quantity: Number(e.qty ?? e.quantity ?? 0),
                    orders: Number(e.orders ?? 0),
                })),
            };
        } else if (raw.depth.buy || raw.depth.sell) {
            depth = { buy: raw.depth.buy || [], sell: raw.depth.sell || [] };
        }
    }

    return {
        symbolToken,
        tradingSymbol: raw.tradingSymbol,
        ltp: raw.ltp,
        high: raw.high,
        low: raw.low,
        open: raw.open,
        close: raw.close,
        avgPrice: raw.avgPrice,
        tradeVolume: raw.tradeVolume,
        netChange: raw.netChange,
        percentChange: raw.percentChange,
        upperCircuit: raw.upperCircuit,
        lowerCircuit: raw.lowerCircuit,
        depth,
        timestamp: raw.timestamp,
    };
}

function scheduleFlush() {
    if (rafScheduled) return;
    rafScheduled = true;

    const flush = () => {
        rafScheduled = false;
        if (tickBuffer.size === 0) return;
        const batch = tickBuffer;
        tickBuffer = new Map();
        tickBatchListeners.forEach((fn) => {
            try { fn(batch); } catch (e) { console.error(e); }
        });
    };

    if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(flush);
    } else {
        setTimeout(flush, 16);
    }
}

function emitToServer(event, payload) {
    if (socket && socket.connected) socket.emit(event, payload);
}

function broadcastStatus() {
    statusListeners.forEach((fn) => {
        try { fn(connected); } catch (e) { console.error(e); }
    });
}


export function connectMarketSocket() {
    if (socket) return socket;

    socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
        connected = true;

        if (tokenRefs.size > 0) {
            const tokens = [];
            tokenRefs.forEach((_count, token) => {
                const exchangeType = tokenExchangeType.get(token);
                if (exchangeType !== undefined) tokens.push({ token, exchangeType });
            });
            if (tokens.length > 0) {
                emitToServer('market:subscribe', { tokens, mode: 3 });
            }
        }
        broadcastStatus();
    });

    socket.on('disconnect', (reason) => {
        connected = false;
        console.warn('[marketSocket] disconnected:', reason);
        broadcastStatus();
    });

    socket.on('connect_error', (err) => {
        connected = false;
        console.warn('[marketSocket] connect_error:', err && err.message);
        broadcastStatus();
    });

    socket.on('price:update', (raw) => {
        const tick = normalizeTick(raw);
        if (!tick) return;
        tickBuffer.set(tick.symbolToken, tick);
        scheduleFlush();
    });

    return socket;
}

export function subscribeTokens(tokens) {
    if (!socket) connectMarketSocket();
    const newOnes = [];
    for (const { token, exchangeType } of tokens) {
        const t = String(token);
        const prev = tokenRefs.get(t) || 0;
        tokenRefs.set(t, prev + 1);
        if (prev === 0) {
            tokenExchangeType.set(t, exchangeType);
            newOnes.push({ token: t, exchangeType });
        }
    }
    if (newOnes.length > 0) {
        emitToServer('market:subscribe', { tokens: newOnes, mode: 3 });
    }
}

export function unsubscribeTokens(tokens) {
    for (const { token } of tokens) {
        const t = String(token);
        const prev = tokenRefs.get(t) || 0;
        if (prev <= 1) {
            tokenRefs.delete(t);
            tokenExchangeType.delete(t);
        } else {
            tokenRefs.set(t, prev - 1);
        }
    }
}

/** Subscribe to rAF-batched tick maps. cb receives a Map<token, LiveTick>. */
export function onTickBatch(cb) {
    tickBatchListeners.add(cb);
    return () => tickBatchListeners.delete(cb);
}

export function onStatusChange(cb) {
    statusListeners.add(cb);
    try { cb(connected); } catch (e) { console.error(e); }
    return () => statusListeners.delete(cb);
}

export function isMarketSocketConnected() {
    return connected;
}

/**
 * Resolves once we know whether to use the socket, so modules don't race the
 * connection on first page load and fire the REST quote prematurely.
 *
 *   - true  → socket is connected (now, or it connected within `timeoutMs`)
 *   - false → still not connected after `timeoutMs` (treat socket as OFF and
 *             fall back to the REST quote, exactly like before)
 *
 * This does NOT change the socket-on/off branching — it only makes the
 * "is the socket available?" check reliable during the brief connect window.
 */
export function waitForConnection(timeoutMs = 1500) {
    if (connected) return Promise.resolve(true);
    if (!socket) connectMarketSocket();

    return new Promise((resolve) => {
        let done = false;
        const finish = (val) => {
            if (done) return;
            done = true;
            if (socket) socket.off('connect', onConnect);
            clearTimeout(timer);
            resolve(val);
        };
        const onConnect = () => finish(true);
        const timer = setTimeout(() => finish(connected), timeoutMs);
        if (socket) socket.on('connect', onConnect);
    });
}

/** Turn { exchangeTokens: { MCX: [...], NFO: [...] } } into the flat
 *  [{ token, exchangeType }] list the singleton wants. */
export function tokensFromExchangeMap(input) {
    const result = [];
    Object.entries((input && input.exchangeTokens) || {}).forEach(
        ([exchange, tokens]) => {
            const exchangeType = EXCHANGE_TO_TYPE[String(exchange).toUpperCase()];
            if (exchangeType === undefined) {
                console.warn(`[marketSocket] unknown exchange "${exchange}" — skipping`);
                return;
            }
            (tokens || []).forEach((t) =>
                result.push({ token: String(t), exchangeType }),
            );
        },
    );
    return result;
}

/** Same idea but used by sagas that have a `{ exchange: 'MCX', tokens: [...] }`
 *  one-tab shape. */
export function tokensFromList(exchange, tokenList) {
    const exchangeType = EXCHANGE_TO_TYPE[String(exchange).toUpperCase()];
    if (exchangeType === undefined) return [];
    return (tokenList || []).map((t) => ({
        token: String(t),
        exchangeType,
    }));
}
